'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, X, Dna, Sparkles, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useChat } from '@/lib/hooks/useChat';

interface DNAFormData {
  zielgruppe: string;
  mission: string;
  unique_selling: string;
  sprache: string;
  brand_history: string;
}

const initialNoGoWords = ['Synergien', 'Paradigmenwechsel', 'Disruption', 'Best Practice', 'Stakeholder', 'Agilität', 'Nachhaltigkeit (generisch)'];

export default function BrandVaultDNA() {
  const [noGoWords, setNoGoWords] = useState<string[]>(initialNoGoWords);
  const [newWord, setNewWord] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { register, handleSubmit, getValues, formState: { errors, isDirty }, trigger } = useForm<DNAFormData>({
    defaultValues: {
      zielgruppe: 'Geschäftsführer und Marketingleiter im deutschen Mittelstand (50–500 MA), die mit begrenztem Budget maximale Wirkung erzielen wollen.',
      mission: 'Wir beweisen, dass Mittelstand und modernes Marketing kein Widerspruch sind. Kein Rauschen. Kein Standard. Nur echter Inhalt.',
      unique_selling: 'Persönliche Expertise trifft industrielles Know-how. Wir sprechen die Sprache unserer Kunden, nicht die der Berater.',
      sprache: 'Deutsch (Duzen, direkt, klar)',
      brand_history: '',
    },
    mode: 'onTouched',
  });

  const { response, isLoading, error, sendMessage } = useChat('GEMINI', 'gemini/gemini-2.5-pro', false);

  React.useEffect(() => {
    if (error) toast.error('Analyse fehlgeschlagen: ' + error.message);
  }, [error]);

  React.useEffect(() => {
    if (!isLoading && response && analyzing) {
      setAnalysisResult(response);
      setAnalyzing(false);
      toast.success('Markenanalyse abgeschlossen!', { description: 'Gemini Pro hat deine Brand-DNA analysiert.' });
    }
  }, [isLoading, response, analyzing]);

  const addNoGoWord = () => {
    if (newWord.trim() && !noGoWords.includes(newWord.trim())) {
      setNoGoWords((prev) => [...prev, newWord.trim()]);
      setNewWord('');
    }
  };

  const handleNoGoKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addNoGoWord();
    }
  };

  const removeNoGoWord = (word: string) => {
    setNoGoWords((prev) => prev.filter((w) => w !== word));
  };

  const onSubmit = (data: DNAFormData) => {
    // TODO: Backend — POST /api/brand-vault/dna with data + noGoWords
    setSaveSuccess(true);
    toast.success('DNA-Editor gespeichert', { description: 'Deine Brand-DNA wurde erfolgreich aktualisiert.' });
    setTimeout(() => setSaveSuccess(false), 4000);
    console.log(data, noGoWords);
  };

  const handleGeminiAnalysis = async () => {
    const valid = await trigger(['zielgruppe', 'mission']);
    if (!valid) {
      toast.error('Bitte fülle zuerst Zielgruppe und Mission aus.');
      return;
    }
    const values = getValues();
    setAnalyzing(true);
    setAnalysisResult('');

    const prompt = `Du bist ein erfahrener Brand-Stratege. Analysiere die folgende Brand-DNA eines deutschen Mittelstandsunternehmens und gib konkrete Empfehlungen für die Content-Strategie.

BRAND-DNA:
Zielgruppe: ${values.zielgruppe}
Mission: ${values.mission}
Alleinstellungsmerkmal: ${values.unique_selling}
Sprach-Stil: ${values.sprache}
Markengeschichte: ${values.brand_history || 'Nicht angegeben'}
No-Go-Wörter: ${noGoWords.join(', ')}

Erstelle eine präzise Analyse (max. 300 Wörter) mit:
1. Stärken der Brand-DNA
2. Lücken und Verbesserungspotenzial
3. 3 konkrete Content-Empfehlungen für Social Media
4. Empfohlener Ton für LinkedIn, Instagram und TikTok

Antworte auf Deutsch, direkt und ohne Floskeln.`;

    sendMessage([{ role: 'user', content: prompt }], {
      temperature: 0.7,
      max_tokens: 1000,
    });
  };

  const fieldClass = (hasError: boolean, isTouched: boolean, value?: string) => {
    if (hasError) return 'textarea-punk border-red-500 focus:border-red-400';
    if (isTouched && !hasError && value) return 'textarea-punk border-green-600 focus:border-green-500';
    return 'textarea-punk';
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="punk-card p-5"
      aria-label="Brand-DNA Editor Formular"
      noValidate
    >
      <div className="flex items-center gap-2 mb-5 pb-4 border-b border-border">
        <Dna size={16} className="text-accent" aria-hidden="true" />
        <h2 className="text-sm font-700 text-white uppercase tracking-widest" id="dna-editor-heading">DNA-Editor</h2>
        {isDirty && !saveSuccess && (
          <span
            className="ml-auto text-xs mono text-yellow-400 border border-yellow-800/40 bg-yellow-950/20 px-2 py-0.5 rounded"
            role="status"
            aria-live="polite"
          >
            Ungespeicherte Änderungen
          </span>
        )}
        {saveSuccess && (
          <span
            className="ml-auto flex items-center gap-1.5 text-xs mono text-green-400 border border-green-800/40 bg-green-950/20 px-2 py-0.5 rounded"
            role="status"
            aria-live="polite"
          >
            <CheckCircle size={11} aria-hidden="true" />
            Gespeichert
          </span>
        )}
      </div>

      {/* Success Banner */}
      {saveSuccess && (
        <div
          className="flex items-center gap-3 p-3 mb-4 bg-green-950/30 border border-green-700/40 rounded"
          role="alert"
          aria-live="assertive"
        >
          <CheckCircle size={15} className="text-green-400 flex-shrink-0" aria-hidden="true" />
          <div>
            <p className="text-xs font-600 text-green-400">Brand-DNA erfolgreich gespeichert!</p>
            <p className="text-xs text-muted">Alle Änderungen wurden übernommen und werden bei der nächsten KI-Generierung verwendet.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="label-punk" htmlFor="zielgruppe">Zielgruppe</label>
          <p className="text-xs text-muted-2 mb-2" id="zielgruppe-desc">Wer soll deine Inhalte lesen? Sei so spezifisch wie möglich.</p>
          <textarea
            id="zielgruppe"
            className={fieldClass(!!errors.zielgruppe, true, getValues('zielgruppe'))}
            rows={4}
            aria-describedby="zielgruppe-desc zielgruppe-error"
            aria-invalid={!!errors.zielgruppe}
            aria-required="true"
            {...register('zielgruppe', { required: 'Zielgruppe ist erforderlich.' })}
          />
          {errors.zielgruppe ? (
            <p
              id="zielgruppe-error"
              className="flex items-center gap-1 text-xs text-red-400 mt-1 mono"
              role="alert"
              aria-live="polite"
            >
              <AlertCircle size={11} aria-hidden="true" />
              {errors.zielgruppe.message}
            </p>
          ) : (
            getValues('zielgruppe') && (
              <p className="flex items-center gap-1 text-xs text-green-500 mt-1 mono" aria-live="polite">
                <CheckCircle size={11} aria-hidden="true" />
                Zielgruppe definiert.
              </p>
            )
          )}
        </div>

        <div>
          <label className="label-punk" htmlFor="mission">Mission Statement</label>
          <p className="text-xs text-muted-2 mb-2" id="mission-desc">Warum existiert dein Unternehmen? In einem Satz.</p>
          <textarea
            id="mission"
            className={fieldClass(!!errors.mission, true, getValues('mission'))}
            rows={4}
            aria-describedby="mission-desc mission-error"
            aria-invalid={!!errors.mission}
            aria-required="true"
            {...register('mission', { required: 'Mission Statement ist erforderlich.' })}
          />
          {errors.mission ? (
            <p
              id="mission-error"
              className="flex items-center gap-1 text-xs text-red-400 mt-1 mono"
              role="alert"
              aria-live="polite"
            >
              <AlertCircle size={11} aria-hidden="true" />
              {errors.mission.message}
            </p>
          ) : (
            getValues('mission') && (
              <p className="flex items-center gap-1 text-xs text-green-500 mt-1 mono" aria-live="polite">
                <CheckCircle size={11} aria-hidden="true" />
                Mission Statement gesetzt.
              </p>
            )
          )}
        </div>

        <div>
          <label className="label-punk" htmlFor="unique_selling">Alleinstellungsmerkmal</label>
          <p className="text-xs text-muted-2 mb-2" id="usp-desc">Was macht dich unverwechselbar?</p>
          <textarea
            id="unique_selling"
            className={fieldClass(!!errors.unique_selling, true, getValues('unique_selling'))}
            rows={3}
            aria-describedby="usp-desc usp-error"
            aria-invalid={!!errors.unique_selling}
            aria-required="true"
            {...register('unique_selling', { required: 'USP ist erforderlich.' })}
          />
          {errors.unique_selling ? (
            <p
              id="usp-error"
              className="flex items-center gap-1 text-xs text-red-400 mt-1 mono"
              role="alert"
              aria-live="polite"
            >
              <AlertCircle size={11} aria-hidden="true" />
              {errors.unique_selling.message}
            </p>
          ) : (
            getValues('unique_selling') && (
              <p className="flex items-center gap-1 text-xs text-green-500 mt-1 mono" aria-live="polite">
                <CheckCircle size={11} aria-hidden="true" />
                USP definiert.
              </p>
            )
          )}
        </div>

        <div>
          <label className="label-punk" htmlFor="sprache">Sprach-Stil</label>
          <p className="text-xs text-muted-2 mb-2" id="sprache-desc">Wie sprichst du deine Zielgruppe an?</p>
          <input
            id="sprache"
            className="input-punk"
            aria-describedby="sprache-desc"
            {...register('sprache')}
          />
        </div>
      </div>

      {/* Brand History — Gemini Large Context Window */}
      <div className="mt-5">
        <label className="label-punk" htmlFor="brand_history">Markengeschichte & Kontext</label>
        <p className="text-xs text-muted-2 mb-2" id="brand-history-desc">
          Nutze Geminis großes Kontextfenster — füge hier ausführliche Markengeschichte, Kampagnen-Hintergründe, Kundenfeedback oder Positionierungsdokumente ein (bis zu 100.000 Zeichen).
        </p>
        <textarea
          id="brand_history"
          className="textarea-punk"
          style={{ minHeight: '120px' }}
          placeholder="Füge hier ausführliche Markeninformationen ein — Unternehmensgeschichte, vergangene Kampagnen, Kundenstimmen, Wettbewerbsanalysen, Positionierungsdokumente..."
          aria-describedby="brand-history-desc"
          {...register('brand_history')}
        />
      </div>

      {/* No-Go Words */}
      <div className="mt-6 pt-5 border-t border-border">
        <label className="label-punk mb-1" id="nogo-label">No-Go-Wörter</label>
        <p className="text-xs text-muted-2 mb-3" id="nogo-desc">Diese Begriffe werden von der KI niemals verwendet.</p>
        <div
          className="flex flex-wrap gap-2 mb-3"
          role="list"
          aria-label="No-Go-Wörter Liste"
        >
          {noGoWords.map((word) => (
            <span
              key={`nogo-${word}`}
              className="inline-flex items-center gap-1.5 bg-red-950/30 border border-red-800/40 text-red-400 text-xs mono px-2.5 py-1 rounded"
              role="listitem"
            >
              {word}
              <button
                type="button"
                onClick={() => removeNoGoWord(word)}
                className="hover:text-red-200 transition-colors focus:outline-none focus:ring-1 focus:ring-red-400 rounded"
                aria-label={`"${word}" aus No-Go-Wörtern entfernen`}
              >
                <X size={10} aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            className="input-punk flex-1 text-xs"
            placeholder="Neues No-Go-Wort eingeben…"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={handleNoGoKeyDown}
            aria-label="Neues No-Go-Wort"
            aria-describedby="nogo-desc"
          />
          <button
            type="button"
            onClick={addNoGoWord}
            className="btn-ghost px-3"
            aria-label="No-Go-Wort hinzufügen"
          >
            <Plus size={14} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Gemini Analysis */}
      <div className="mt-6 pt-5 border-t border-border">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs font-700 text-white uppercase tracking-wider">KI-Markenanalyse</p>
            <p className="text-xs text-muted">Gemini 2.5 Pro analysiert deine Brand-DNA</p>
          </div>
          <button
            type="button"
            onClick={handleGeminiAnalysis}
            disabled={analyzing || isLoading}
            className="btn-ghost text-xs"
            aria-label="Brand-DNA mit Gemini AI analysieren"
            aria-busy={analyzing || isLoading}
          >
            {analyzing || isLoading ? (
              <>
                <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                Analysiert…
              </>
            ) : (
              <>
                <Sparkles size={12} aria-hidden="true" />
                Mit Gemini analysieren
              </>
            )}
          </button>
        </div>

        {analysisResult && (
          <div
            className="p-4 bg-surface border border-accent/20 rounded fade-in"
            role="region"
            aria-label="Gemini Markenanalyse Ergebnis"
            aria-live="polite"
          >
            <p className="text-xs font-600 text-accent mb-2 uppercase tracking-wider">Gemini Pro Analyse</p>
            <div className="text-xs text-white leading-relaxed whitespace-pre-wrap">{analysisResult}</div>
          </div>
        )}
      </div>

      <div className="mt-5 flex justify-end pt-5 border-t border-border">
        <button
          type="submit"
          className="btn-primary text-xs"
          aria-label="Brand-DNA Formular speichern"
        >
          DNA speichern
        </button>
      </div>
    </form>
  );
}